import { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { PlugZap, Sun } from "lucide-react";
import { EV_FUNZ, EV_STATO_TXT } from "./SkyScene";
import { pw, kwh } from "./clienteApi";

const ORD = { attiva: 3, attesa: 2, in_pausa: 1 };
const eur = (v) => (v == null ? "—" : "€ " + Number(v).toFixed(2).replace(".", ","));
const hhmm = (iso) => { try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }); } catch { return ""; } };
const fdt = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", timeZone: "Europe/Rome" }) + " " + hhmm(iso); } catch { return ""; } };
const durata = (s) => { if (!s) return null; const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m`; };

const Tile = ({ l, v, c }) => (
  <div><span className="block text-xs font-semibold text-slate-500">{l}</span><b className="block text-2xl font-extrabold xl:text-3xl" style={{ color: c || "#1E293B" }}>{v}</b></div>
);
const Pill = ({ it }) => {
  const meta = EV_FUNZ[it.key]; if (!meta) return null;
  const live = it.stato === "attiva"; const col = live ? meta.color : "#94A3B8";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold uppercase tracking-wide ring-1 ${live ? "" : "opacity-70"}`}
      style={{ color: col, background: `${col}14`, borderColor: `${col}33` }} data-testid={`schermo-ev-funzione-${it.key}`}>
      {live && <span className="h-2 w-2 rounded-full" style={{ background: col, boxShadow: `0 0 6px ${col}` }} />}
      <meta.icon className="h-4 w-4" />{meta.label}{!live && <span className="font-semibold normal-case tracking-normal text-slate-400">· {EV_STATO_TXT[it.stato]}</span>}
    </span>
  );
};

// Slot "Stazioni EV" della Vista Schermo. In carica: dati in tempo reale (refresh ogni 60s) + grafico
// della ricarica in corso. A riposo: dati e grafico dell'ULTIMA ricarica. Con più colonnine ruota ogni 8s.
export function SchermoEv({ src, tick }) {
  const [ev, setEv] = useState(null);
  const [idx, setIdx] = useState(0);
  const [meter, setMeter] = useState(null);

  useEffect(() => { if (src?.ev) src.ev().then(setEv).catch(() => {}); }, [src, tick]);

  const stazioni = (ev && ev.disponibile && ev.stazioni) || [];
  const inCaricaQualcuna = stazioni.some((s) => s.in_carica);

  // in carica: aggiornamento in tempo reale ogni 60s
  useEffect(() => {
    if (!inCaricaQualcuna || !src?.ev) return;
    const id = setInterval(() => src.ev().then(setEv).catch(() => {}), 60000);
    return () => clearInterval(id);
  }, [inCaricaQualcuna, src]);

  useEffect(() => {
    if (stazioni.length <= 1) { setIdx(0); return; }
    const id = setInterval(() => setIdx((i) => (i + 1) % stazioni.length), 8000);
    return () => clearInterval(id);
  }, [stazioni.length]);

  const st = stazioni.length ? stazioni[Math.min(idx, stazioni.length - 1)] : null;
  const last = st?.sessioni_logiche?.[0] || null;
  const live = !!st?.in_carica;

  // grafico: potenza dell'ultima sessione logica (in corso se in carica, altrimenti l'ultima conclusa)
  useEffect(() => {
    const ids = (last?.parti_ids || []).join(",");
    if (!ids || !src?.evMeterMulti) { setMeter(null); return; }
    src.evMeterMulti(ids).then(setMeter).catch(() => setMeter(null));
  }, [st?.id, st?.session_id, live, idx, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stazioni.length) return null;
  const badges = (st.funzioni || []).slice().sort((a, b) => ORD[b.stato] - ORD[a.stato]);
  const points = (meter?.points || []).map((p) => ({ t: hhmm(p.ts), kw: p.power_w != null ? +(p.power_w / 1000).toFixed(2) : null }));
  const haRisp = last && last.risparmio_pun > 0;

  return (
    <section className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-100" data-testid="schermo-ev">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-base font-extrabold text-slate-800"><PlugZap className="h-5 w-5 text-sky-600" />Ricarica auto</h3>
        {stazioni.length > 1 && (
          <span className="flex gap-1.5" data-testid="schermo-ev-dots">
            {stazioni.map((_, i) => <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ background: i === idx ? "#0EA5E9" : "#CBD5E1" }} />)}
          </span>
        )}
      </div>
      <div key={idx} style={{ animation: "ssFrame 0.5s cubic-bezier(0.4,0,0.2,1)" }}>
        <style>{`@keyframes ssFrame{0%{opacity:0;transform:translateY(6px)}100%{opacity:1;transform:translateY(0)}}`}</style>
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 truncate text-xl font-extrabold text-slate-900 xl:text-2xl" data-testid="schermo-ev-nome">{st.nome}</span>
          <span className={`shrink-0 rounded-full px-3 py-1 text-sm font-bold ${live ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`} data-testid="schermo-ev-stato">{live ? "In ricarica" : "Ferma"}</span>
        </div>

        {live ? (
          <div className="mt-3 grid grid-cols-3 gap-3" data-testid="schermo-ev-tiles-live">
            <Tile l="Potenza" v={pw(st.potenza_w)} />
            <Tile l="Dal sole" v={st.solar_pct == null ? "—" : `${st.solar_pct}%`} c="#EAB308" />
            <Tile l="Caricato ora" v={last ? kwh(last.energia_kwh) : "—"} />
          </div>
        ) : last ? (
          <>
            <p className="mt-1 text-sm text-slate-500" data-testid="schermo-ev-ultima-quando">Ultima ricarica · {fdt(last.inizio)}{last.fine ? ` → ${hhmm(last.fine)}` : ""}{last.durata_s ? ` · ${durata(last.durata_s)}` : ""}</p>
            <div className="mt-2 grid grid-cols-3 gap-3" data-testid="schermo-ev-tiles-ultima">
              <Tile l="Energia" v={kwh(last.energia_kwh)} />
              <Tile l="Spesa (PUN)" v={last.costo_pun != null ? eur(last.costo_pun) : "—"} />
              <Tile l="Risparmio" v={haRisp ? eur(last.risparmio_pun) : "—"} c={haRisp ? "#16A34A" : undefined} />
            </div>
          </>
        ) : <p className="mt-2 text-sm text-slate-400" data-testid="schermo-ev-nessuna">Nessuna ricarica registrata</p>}

        {badges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="schermo-ev-funzioni">{badges.map((it) => <Pill key={it.key} it={it} />)}</div>
        )}

        {last && (
          <div className="mt-3 border-t border-slate-100 pt-2" data-testid="schermo-ev-grafico">
            <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-400">
              {live && <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-500" />}
              {live ? "In tempo reale" : "Ultima ricarica"} · potenza (kW){!live && haRisp && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold normal-case tracking-normal text-emerald-700" data-testid="schermo-ev-risparmio"><Sun className="h-3 w-3" />risparmiati {eur(last.risparmio_pun)} col PUN</span>}
            </div>
            <div className="h-24 w-full xl:h-28">
              {points.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={points} margin={{ top: 4, right: 6, left: -16, bottom: 0 }}>
                    <defs><linearGradient id="evScgrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.45} /><stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="t" tick={{ fontSize: 10, fill: "#94A3B8" }} minTickGap={34} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={34} />
                    <Tooltip formatter={(v) => [`${v} kW`, "Potenza"]} />
                    <Area type="monotone" dataKey="kw" stroke="#0EA5E9" strokeWidth={2} fill="url(#evScgrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <p className="flex h-full items-center justify-center text-center text-xs text-slate-400">Nessun dato di potenza</p>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
